/**
 * PDF Generator Service - Creates professional PDF documents using jsPDF
 */
import { jsPDF } from 'jspdf';
import { PDFExportData, PageData, TileData, OrganizationData, TOCEntry } from './PDFDataService';
import { api } from '../lib/api';

interface PDFStyles {
  colors: {
    primary: [number, number, number];
    secondary: [number, number, number];
    text: [number, number, number];
    lightText: [number, number, number];
    accent: [number, number, number];
  };
  fonts: {
    title: number;
    heading1: number;
    heading2: number;
    heading3: number;
    body: number;
    small: number;
  };
  spacing: {
    section: number;
    paragraph: number;
    line: number;
  };
}

interface PDFPosition {
  x: number;
  y: number;
}

export class PDFGeneratorService {
  private doc: jsPDF;
  private styles: PDFStyles;
  private currentY: number;
  private pageHeight: number;
  private pageWidth: number;
  private margin: number;
  private contentWidth: number;
  private tocEntries: Array<{ title: string; page: number; level: number }> = [];
  private currentPageNumber: number = 1;
  private sectionCounters: number[] = [0, 0, 0, 0, 0]; // Support up to 5 levels of nesting
  private currentIndentationLevel = 0; // Track indentation level across paragraphs

  constructor() {
    this.doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    this.pageHeight = this.doc.internal.pageSize.height;
    this.pageWidth = this.doc.internal.pageSize.width;
    this.margin = 20;
    this.contentWidth = this.pageWidth - (this.margin * 2);
    this.currentY = this.margin;

    this.styles = {
      colors: {
        primary: [37, 99, 235],    // #2563eb
        secondary: [100, 116, 139], // #64748b  
        text: [30, 41, 59],        // #1e293b
        lightText: [100, 116, 139], // #64748b
        accent: [5, 150, 105]      // #059669
      },
      fonts: {
        title: 24,
        heading1: 18,
        heading2: 16,
        heading3: 14,
        body: 11,
        small: 9
      },
      spacing: {
        section: 15,
        paragraph: 8,
        line: 5
      }
    };
  }

  /**
   * Helper method to find a tile by name recursively
   */
  private findTileByName(pageData: any, tileName: string): any {
    // Check tiles in current page
    const tile = pageData.tiles.find((t: any) => t.title === tileName);
    if (tile) return tile;
    
    // Check child pages recursively
    for (const child of pageData.children) {
      const found = this.findTileByName(child, tileName);
      if (found) return found;
    }
    
    return null;
  }

  /**
   * Debug helper to scan all tiles for content
   */
  private debugAllTilesWithContent(pageData: any, level: number = 0): void {
    // Debug method - can be removed in production
  }

  /**
   * Main method to generate PDF from export data
   */
  async generatePDF(exportData: PDFExportData, filename: string): Promise<void> {
    try {
      console.log('🎨 Starting PDF generation...');
      

      // Generate cover page
      await this.generateCoverPage(exportData.organization);
      
      // Generate content pages and collect TOC entries
      await this.generateContentPages(exportData.content);
      
      // Generate table of contents at the end of the document
      const tocStartPage = this.generateTableOfContentsAtEnd();
      
      // Add ToC link to cover page
      this.addTocLinkToCoverPage(tocStartPage);
      
      // Add page numbers to all pages
      this.addPageNumbers();
      
      // Save the PDF
      this.doc.save(`${filename}.pdf`);
      
      console.log('✅ PDF generation complete');
      
    } catch (error) {
      console.error('❌ PDF generation failed:', error);
      throw new Error(`PDF generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Add logo placeholder with organization initial
   */
  private addLogoPlaceholder(organization: OrganizationData, titleY: number): void {
    this.doc.setFillColor(37, 99, 235); // Blue circle
    this.doc.circle(this.pageWidth / 2, titleY - 30, 15, 'F');
    
    this.doc.setTextColor(255, 255, 255);
    this.doc.setFontSize(16);
    this.doc.setFont('helvetica', 'bold');
    const initial = organization.companyName ? organization.companyName.charAt(0).toUpperCase() : 'N';
    this.doc.text(initial, this.pageWidth / 2, titleY - 25, { align: 'center' });
    
    console.log('✅ Logo placeholder added with initial:', initial);
  }

  /**
   * Load an image from URL and add it to the PDF
   */
  private async loadAndAddImage(imageUrl: string, x: number, y: number, width: number, height: number): Promise<boolean> {
    try {
      console.log('🖼️ Loading image from URL:', imageUrl);
      
      // Create a promise to load the image
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous'; // Handle CORS
        
        img.onload = () => {
          try {
            console.log('✅ Image loaded successfully:', { width: img.width, height: img.height });
            
            // Create a canvas to convert image to base64
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            if (!ctx) {
              console.error('❌ Could not get canvas context');
              resolve(false);
              return;
            }
            
            // Set canvas size to match desired dimensions
            canvas.width = img.width;
            canvas.height = img.height;
            
            // Draw image to canvas
            ctx.drawImage(img, 0, 0);
            
            // Get base64 data
            const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
            
            // Calculate aspect ratio to maintain proportions
            const aspectRatio = img.width / img.height;
            let finalWidth = width;
            let finalHeight = height;
            
            if (aspectRatio > 1) {
              // Landscape - fit to width
              finalHeight = width / aspectRatio;
            } else {
              // Portrait or square - fit to height
              finalWidth = height * aspectRatio;
            }
            
            // Center the image
            const centerX = x - finalWidth / 2;
            const centerY = y - finalHeight / 2;
            
            // Add image to PDF
            this.doc.addImage(dataUrl, 'JPEG', centerX, centerY, finalWidth, finalHeight);
            
            console.log('✅ Image added to PDF:', { x: centerX, y: centerY, width: finalWidth, height: finalHeight });
            resolve(true);
          } catch (error) {
            console.error('❌ Error processing loaded image:', error);
            resolve(false);
          }
        };
        
        img.onerror = (error) => {
          console.error('❌ Error loading image:', error);
          resolve(false);
        };
        
        // Set a timeout to avoid hanging
        setTimeout(() => {
          console.warn('⏰ Image loading timeout');
          resolve(false);
        }, 10000); // 10 second timeout
        
        img.src = imageUrl;
      });
    } catch (error) {
      console.error('❌ Error in loadAndAddImage:', error);
      return false;
    }
  }

  /**
   * Generate the cover page with organization branding
   */
  private async generateCoverPage(organization: OrganizationData): Promise<void> {
    console.log('📄 Generating cover page...');
    
    // Set background color
    this.doc.setFillColor(248, 250, 252); // Light gray background
    this.doc.rect(0, 0, this.pageWidth, this.pageHeight, 'F');
    
    // Main title area
    const titleY = this.pageHeight * 0.25;
    
    // Organization logo - try to load actual logo, fallback to placeholder
    if (organization.logoUrl) {
      try {
        
        // Try to load the actual image
        const logoLoaded = await this.loadAndAddImage(organization.logoUrl, this.pageWidth / 2, titleY - 30, 60, 30);
        
        if (!logoLoaded) {
          console.log('⚠️ Failed to load logo image, using placeholder');
          this.addLogoPlaceholder(organization, titleY);
        }
      } catch (error) {
        console.error('❌ Failed to load organization logo:', error);
        // Fall back to initial placeholder
        this.addLogoPlaceholder(organization, titleY);
      }
    } else {
      console.log('📝 No logo URL provided, using initial placeholder');
      this.addLogoPlaceholder(organization, titleY);
    }
    
    // Company name
    this.doc.setTextColor(...this.styles.colors.primary);
    this.doc.setFontSize(this.styles.fonts.title);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(organization.companyName, this.pageWidth / 2, titleY, { align: 'center' });
    
    // Document title
    this.doc.setTextColor(...this.styles.colors.text);
    this.doc.setFontSize(this.styles.fonts.heading1);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text('Content Export', this.pageWidth / 2, titleY + 15, { align: 'center' });
    
    // Company motto (if available)
    let currentY = titleY + 25;
    if (organization.companyMotto) {
      this.doc.setTextColor(...this.styles.colors.secondary);
      this.doc.setFontSize(this.styles.fonts.body);
      this.doc.setFont('helvetica', 'italic');
      this.doc.text(`"${organization.companyMotto}"`, this.pageWidth / 2, currentY, { align: 'center' });
      currentY += 15;
    }
    
    // Organization Profile Information Box
    const profileY = currentY + 20;
    
    // Calculate box height based on content
    const profileLines = [];
    
    if (organization.companyWebsite) {
      profileLines.push(`Website: ${organization.companyWebsite}`);
    }
    
    if (organization.companyPhone) {
      profileLines.push(`Phone: ${organization.companyPhone}`);
    }
    
    if (organization.companyABN) {
      profileLines.push(`ABN: ${organization.companyABN}`);
    }
    
    if (organization.companyACN) {
      profileLines.push(`ACN: ${organization.companyACN}`);
    }
    
    if (organization.otherInfo) {
      profileLines.push(`Other Info: ${organization.otherInfo}`);
    }
    
    profileLines.push(`Organization ID: ${organization.id}`);
    
    const boxHeight = Math.max(60, 25 + (profileLines.length * 6));
    
    this.doc.setDrawColor(37, 99, 235);
    this.doc.setFillColor(249, 250, 251);
    this.doc.roundedRect(this.margin, profileY, this.contentWidth, boxHeight, 3, 3, 'FD');
    
    // Profile title
    this.doc.setTextColor(...this.styles.colors.primary);
    this.doc.setFontSize(this.styles.fonts.heading3);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Organization Profile', this.margin + 5, profileY + 10);
    
    // Profile information
    this.doc.setTextColor(...this.styles.colors.text);
    this.doc.setFontSize(this.styles.fonts.small);
    this.doc.setFont('helvetica', 'normal');
    
    profileLines.forEach((line, index) => {
      this.doc.text(line, this.margin + 5, profileY + 20 + (index * 6));
    });
    
    console.log('📋 Added organization profile with', profileLines.length, 'fields');
    
    // Export information box (moved up slightly to make room for ToC link)
    const infoY = this.pageHeight * 0.70;
    this.doc.setDrawColor(229, 231, 235);
    this.doc.setFillColor(249, 250, 251);
    this.doc.roundedRect(this.margin, infoY, this.contentWidth, 35, 3, 3, 'FD');
    
    // Export info title
    this.doc.setTextColor(...this.styles.colors.text);
    this.doc.setFontSize(this.styles.fonts.body);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Export Information', this.margin + 5, infoY + 8);
    
    this.doc.setFontSize(this.styles.fonts.small);
    this.doc.setFont('helvetica', 'normal');
    
    const infoLines = [
      `Generated: ${organization.exportDate}`,
      `Exported by: ${organization.exportedBy}`,
    ];
    
    infoLines.forEach((line, index) => {
      this.doc.text(line, this.margin + 5, infoY + 18 + (index * 6));
    });
    
    // Note: Page numbering is handled by addNewPage() method, not manually
  }



  /**
   * Generate table of contents at the end of the document
   */
  private generateTableOfContentsAtEnd(): number {
    console.log('📋 Generating table of contents at end of document');
    console.log('📋 TOC entries to process:', this.tocEntries.length);
    
    // Add new page for ToC at the end
    this.addNewPage();
    const tocStartPage = this.currentPageNumber;
    
    // Generate ToC content
    this.generateTableOfContents(tocStartPage);
    
    return tocStartPage;
  }

  /**
   * Add a clickable link to the Table of Contents on the cover page
   */
  private addTocLinkToCoverPage(tocStartPage: number): void {
    console.log(`📋 Adding ToC link to cover page, pointing to page ${tocStartPage}`);
    
    // Go back to cover page (page 1)
    this.doc.setPage(1);
    
    // Position the ToC link below the export information box
    // Export info box is at 70% of page height with 35pt height
    const exportBoxY = this.pageHeight * 0.70;
    const exportBoxHeight = 35;
    const linkY = exportBoxY + exportBoxHeight + 15; // 15pt spacing below export box
    
    const linkText = `→ Table of Contents (Page ${tocStartPage})`;
    
    // Use consistent styling with the rest of the document
    this.doc.setTextColor(...this.styles.colors.text); // Same as other text
    this.doc.setFontSize(this.styles.fonts.small); // Same as export info text
    this.doc.setFont('helvetica', 'normal');
    
    // Position aligned with the left edge of the export details box
    const linkX = this.margin + 5; // Same as export info text indentation
    
    // Add the clickable link
    const linkWidth = this.doc.getTextWidth(linkText);
    this.doc.link(linkX, linkY - 3, linkWidth, this.styles.fonts.small + 2, { pageNumber: tocStartPage });
    this.doc.text(linkText, linkX, linkY);
    
    console.log(`📋 ToC link added at (${linkX}, ${linkY}) pointing to page ${tocStartPage}`);
  }

  /**
   * Generate table of contents on dedicated pages
   */
  private generateTableOfContents(tocStartPage: number): void {
    console.log('📋 Generating table of contents on page', tocStartPage);
    console.log('📋 TOC entries to process:', this.tocEntries.length);
    
    // Go to first TOC page and clear it
    this.doc.setPage(tocStartPage);
    
    // Clear the page by drawing a white rectangle
    this.doc.setFillColor(255, 255, 255);
    this.doc.rect(0, 0, this.pageWidth, this.pageHeight, 'F');
    
    let y = this.margin + 20;
    
    // TOC Title (only on first page)
    this.doc.setTextColor(...this.styles.colors.primary);
    this.doc.setFontSize(this.styles.fonts.heading1);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Table of Contents', this.margin, y);
    
    y += this.styles.spacing.section;
    
    // TOC Entries
    this.doc.setFont('helvetica', 'normal');
    
    this.tocEntries.forEach((entry, index) => {
      // Check if we need a new page
      if (y > this.pageHeight - this.margin - 15) {
        // Add a new page for ToC continuation
        this.doc.addPage();
        
        // Clear the page
        this.doc.setFillColor(255, 255, 255);
        this.doc.rect(0, 0, this.pageWidth, this.pageHeight, 'F');
        
        y = this.margin + 10;
        
        // Add "Table of Contents (continued)" header
        this.doc.setTextColor(...this.styles.colors.text);
        this.doc.setFontSize(this.styles.fonts.body);
        this.doc.setFont('helvetica', 'italic');
        this.doc.text('Table of Contents (continued)', this.margin, y);
        y += this.styles.spacing.paragraph;
        
        console.log(`📋 Added new ToC continuation page`);
      }
      
      const indent = entry.level * 8;
      const fontSize = entry.level === 0 ? this.styles.fonts.body + 1 : this.styles.fonts.body - 1;
      
      this.doc.setFontSize(fontSize);
      this.doc.setTextColor(...this.styles.colors.text); // Use consistent text color for all ToC entries
      this.doc.setFont('helvetica', entry.level === 0 ? 'bold' : 'normal');
      
      // Truncate long titles
      let displayTitle = entry.title;
      if (displayTitle.length > 60) {
        displayTitle = displayTitle.substring(0, 57) + '...';
      }
      
      // Title with clickable link
      const titleX = this.margin + indent;
      const titleWidth = this.doc.getTextWidth(displayTitle);
      
      // Add clickable link to the title
      this.doc.link(titleX, y - fontSize/2, titleWidth, fontSize, { pageNumber: entry.page });
      this.doc.text(displayTitle, titleX, y);
      
      // Page number (right-aligned) - now matches actual page numbers
      this.doc.text(entry.page.toString(), this.pageWidth - this.margin, y, { align: 'right' });
      
      // Dotted line
      const pageNumWidth = this.doc.getTextWidth(entry.page.toString());
      const dotsStart = this.margin + indent + titleWidth + 2;
      const dotsEnd = this.pageWidth - this.margin - pageNumWidth - 2;
      
      if (dotsEnd > dotsStart) {
        this.doc.setFontSize(this.styles.fonts.small - 1);
        this.doc.setFont('helvetica', 'normal');
        const dotCount = Math.floor((dotsEnd - dotsStart) / 1.5);
        const dots = '.'.repeat(Math.max(0, dotCount));
        this.doc.text(dots, dotsStart, y);
      }
      
      y += entry.level === 0 ? 8 : 6;
    });
    
    console.log('✅ Table of contents generated successfully');
  }

  /**
   * Generate content pages from page hierarchy
   */
  private async generateContentPages(pageData: PageData): Promise<void> {
    console.log('📝 Generating content pages...');
    
    // Reset indentation level at the start of content generation
    this.currentIndentationLevel = 0;
    
    // Add new page for content
    this.addNewPage();
    
    // Process the page hierarchy recursively
    await this.processPageContent(pageData, 1);
  }

  /**
   * Recursively process page content
   */
  private async processPageContent(pageData: PageData, level: number): Promise<void> {
    // Add page break before each page at any level (except the very first page)
    // Skip page break only for the very first content page
    if (this.tocEntries.length > 0 || this.currentY > this.margin + 50) {
      console.log('📄 Adding page break before page:', pageData.title, `(level ${level})`);
      this.addNewPage();
    }
    
    // Add page title to TOC and document
    const pageNumber = this.currentPageNumber;
    this.tocEntries.push({
      title: pageData.title,
      page: pageNumber,
      level: level - 1
    });
    
    // Page title
    this.addHeading(pageData.title, level, true);
    
    // Page description
    if (pageData.description) {
      this.addParagraph(pageData.description);
    }
    
    // Process tiles in order
    
    for (const tile of pageData.tiles) {
      await this.processTile(tile, level + 1);
    }
    
    // Process child pages
    for (const childPage of pageData.children) {
      await this.processPageContent(childPage, level + 1);
    }
  }

  /**
   * Process individual tile based on its type
   */
  private async processTile(tile: TileData, level: number): Promise<void> {
    
    // Skip shortcut tiles entirely - they're just internal links
    if (tile.type === 'shortcut') {
      console.log('⏭️ Skipping shortcut tile:', tile.title);
      return;
    }
    
    // Skip nested_page tiles entirely - their content is already included in the page hierarchy
    if (tile.type === 'nested_page') {
      console.log('⏭️ Skipping nested page tile:', tile.title, '- content already included in page hierarchy');
      return;
    }
    
    // Add tile to TOC if it should be included
    if (this.shouldIncludeInTOC(tile)) {
      this.tocEntries.push({
        title: tile.title,
        page: this.currentPageNumber,
        level: level - 1
      });
    }
    
    // Add tile title as heading
    this.addHeading(tile.title, level);
    
    // Add tile description if available
    if (tile.description) {
      this.addParagraph(tile.description, true); // Italic for descriptions
    }
    
    // Process tile content based on type
    switch (tile.type) {
      case 'content':
      case 'roadmap':
        await this.processContentTile(tile);
        break;
        
      case 'form':
        this.processFormTile(tile);
        break;
        
      case 'external_link':
        this.processExternalLinkTile(tile);
        break;
        
      case 'register':
        this.processRegisterTile(tile);
        break;
        
      case 'workflow':
        this.processWorkflowTile(tile);
        break;
        
      default:
        this.processUnknownTile(tile);
    }
    
    this.addSpacing(this.styles.spacing.section);
  }

  /**
   * Process content and roadmap tiles
   */
  private async processContentTile(tile: TileData): Promise<void> {
    
    // Try different ways to access the rich text content
    let richTextContent = null;
    
    if (tile.content) {
      // Try most common structures - content.body is the primary field based on schema
      if (tile.content.body) {
        richTextContent = tile.content.body;
      } else if (tile.content.content) {
        richTextContent = tile.content.content;
      } else if (typeof tile.content === 'string') {
        richTextContent = tile.content;
      } else if (tile.content.richText) {
        richTextContent = tile.content.richText;
      } else if (tile.content.data?.content) {
        richTextContent = tile.content.data.content;
      } else if (tile.content.data?.body) {
        richTextContent = tile.content.data.body;
      }
    }
    
    if (richTextContent && richTextContent.length > 0) {
      await this.addRichText(richTextContent);
    } else {
      console.log('⚠️ No rich text content found for tile:', tile.title);
      this.addParagraph('(No content available)');
    }
  }

  /**
   * Process form tiles
   */
  private processFormTile(tile: TileData): void {
    if (tile.content?.fields) {
      this.addParagraph('Form Fields:');
      
      tile.content.fields.forEach((field: any, index: number) => {
        const fieldText = `${index + 1}. ${field.label || 'Untitled Field'} (${field.type || 'text'})`;
        this.addParagraph(fieldText);
        
        if (field.required) {
          this.addParagraph('   * Required field');
        }
        
        if (field.placeholder) {
          this.addParagraph(`   * Placeholder: "${field.placeholder}"`);
        }
      });
    } else {
      this.addParagraph('(Form structure not available)');
    }
  }

  /**
   * Process external link tiles
   */
  private processExternalLinkTile(tile: TileData): void {
    const url = tile.config?.url;
    if (url) {
      this.addParagraph(`Link: ${url}`);
      // TODO: Make this a clickable link in the PDF
    } else {
      this.addParagraph('(No URL specified)');
    }
  }

  /**
   * Process shortcut tiles
   */
  private processShortcutTile(tile: TileData): void {
    const linkedItem = tile.content?.linkedItem;
    if (linkedItem) {
      this.addParagraph(`Shortcut to: ${linkedItem.title || 'Untitled'} (${linkedItem.type || 'unknown'})`);
    } else {
      this.addParagraph('(Shortcut target not available)');
    }
  }

  /**
   * Process register tiles
   */
  private processRegisterTile(tile: TileData): void {
    this.addParagraph('Register/Database tile - contains structured data records.');
  }

  /**
   * Process workflow tiles
   */
  private processWorkflowTile(tile: TileData): void {
    this.addParagraph('Workflow tile - contains process automation logic.');
  }

  /**
   * Process nested page tiles - these should not fetch additional data
   * The nested page structure is already built by PDFDataService
   */
  private async processNestedPageTile(tile: TileData, level: number): Promise<void> {
    console.log('🔗 Processing nested page tile:', tile.title);
    console.log('⚠️ Note: Nested page tiles should be handled by the page hierarchy, not as individual tiles');
    
    // Nested page tiles should not be processed as individual content
    // The linked pages are already included in the page hierarchy by PDFDataService
    // This method should only add a reference or skip entirely
    
    this.addParagraph(`→ See section: ${tile.title}`);
    this.addSpacing(this.styles.spacing.section);
  }

  /**
   * Process unknown tile types
   */
  private processUnknownTile(tile: TileData): void {
    console.log('⚠️ Unknown tile type:', tile.type, 'for tile:', tile.title);
    this.addParagraph(`Unknown tile type: ${tile.type}`);
  }

  /**
   * Add a heading to the document
   */
  private addHeading(text: string, level: number, isPageTitle: boolean = false): void {
    this.checkPageBreak(25);
    
    let fontSize: number;
    let color: [number, number, number];
    let indentation: number = 0;
    
    if (isPageTitle) {
      fontSize = this.styles.fonts.heading1;
      color = this.styles.colors.primary;
      indentation = 0;
      
      // Add some extra space before page titles
      if (this.currentY > this.margin + 10) {
        this.addSpacing(this.styles.spacing.section);
      }
    } else {
      // Calculate indentation and styling based on level
      indentation = Math.max(0, (level - 2) * 4); // 4 points per level (halved from 8)
      
      switch (level) {
        case 2:
          fontSize = this.styles.fonts.heading2;
          color = [60, 80, 120]; // Darker blue
          break;
        case 3:
          fontSize = this.styles.fonts.heading3;
          color = [80, 100, 140]; // Medium blue
          break;
        case 4:
          fontSize = this.styles.fonts.body + 3;
          color = [100, 120, 160]; // Lighter blue
          break;
        case 5:
          fontSize = this.styles.fonts.body + 2;
          color = [120, 140, 180]; // Even lighter blue
          break;
        default:
          fontSize = this.styles.fonts.body + 1;
          color = [140, 160, 200]; // Very light blue
      }
    }
    
    // No indentation lines - keep it clean
    
    this.doc.setTextColor(...color);
    this.doc.setFontSize(fontSize);
    this.doc.setFont('helvetica', 'bold');
    
    // Generate section number
    const sectionNumber = this.generateSectionNumber(level, isPageTitle);
    const fullText = sectionNumber ? `${sectionNumber}. ${text}` : text;
    
    // Add text with proper indentation
    const textX = this.margin + indentation;
    this.doc.text(fullText, textX, this.currentY, { maxWidth: this.contentWidth - indentation });
    this.currentY += this.styles.spacing.paragraph;
    
    // Add a subtle line under page titles
    if (isPageTitle) {
      this.doc.setDrawColor(229, 231, 235);
      this.doc.setLineWidth(0.5);
      this.doc.line(this.margin, this.currentY - 2, this.margin + this.contentWidth, this.currentY - 2);
      this.addSpacing(5);
    }
  }

  /**
   * Add a paragraph to the document
   */
  private addParagraph(text: string, italic: boolean = false): void {
    
    this.checkPageBreak(10);
    
    // Process level markers to determine indentation for this paragraph
    let currentLevel = 0;
    let paragraphIndent = 0;
    let cleanText = text;
    
    // Process markers using a much simpler approach to avoid text corruption
    // First, replace all INDENT markers with spaces
    cleanText = cleanText.replace(/§INDENT§/g, '    ');
    
    // Then process LEVEL markers sequentially to track indentation level
    while (cleanText.includes('§LEVEL_UP§') || cleanText.includes('§LEVEL_DOWN§')) {
      const levelUpIndex = cleanText.indexOf('§LEVEL_UP§');
      const levelDownIndex = cleanText.indexOf('§LEVEL_DOWN§');
      
      let nextIndex = -1;
      let isLevelUp = false;
      
      if (levelUpIndex !== -1 && (levelDownIndex === -1 || levelUpIndex < levelDownIndex)) {
        nextIndex = levelUpIndex;
        isLevelUp = true;
      } else if (levelDownIndex !== -1) {
        nextIndex = levelDownIndex;
        isLevelUp = false;
      }
      
      if (nextIndex === -1) break;
      
      if (isLevelUp) {
        currentLevel++;
        cleanText = cleanText.replace('§LEVEL_UP§', '');
      } else {
        currentLevel = Math.max(0, currentLevel - 1);
        cleanText = cleanText.replace('§LEVEL_DOWN§', '');
      }
    }
    
    // Determine paragraph indentation from the final current level
    paragraphIndent = currentLevel;
    
    const indentWidth = paragraphIndent * this.doc.getTextWidth('            '); // 12 spaces per level
    const availableWidth = this.contentWidth - indentWidth;
    
    this.doc.setTextColor(...this.styles.colors.text);
    this.doc.setFontSize(this.styles.fonts.body);
    this.doc.setFont('helvetica', italic ? 'italic' : 'normal');
    
    // Split text into lines that fit within the available width (accounting for indentation)
    const lines = this.doc.splitTextToSize(cleanText, availableWidth);
    
    lines.forEach((line: string) => {
      this.checkPageBreak(5);
      this.doc.text(line, this.margin + indentWidth, this.currentY);
      this.currentY += this.styles.spacing.line;
    });
    
    this.currentY += this.styles.spacing.paragraph - this.styles.spacing.line;
  }

  /**
   * Add formatted paragraph with HTML formatting preserved
   */
  private async addFormattedParagraph(plainText: string, htmlText: string): Promise<void> {
    
    this.checkPageBreak(10);
    
    // Parse the HTML to extract formatting information
    const formattedSegments = this.parseFormattedText(htmlText);
    
    if (formattedSegments.length === 0) {
      // Fallback to plain text if no formatting found
      this.addParagraph(plainText);
      return;
    }
    
    await this.processFormattedSegments(formattedSegments);
  }
  
  private async processFormattedSegments(formattedSegments: Array<{text: string, bold?: boolean, italic?: boolean, color?: [number, number, number]}>): Promise<void> {
    
    // Process level markers to determine current indentation level for this paragraph
    // Start with the class-level indentation state from previous paragraphs
    let paragraphIndent = this.currentIndentationLevel;
    
      // First pass: Process level markers, but handle trailing LEVEL_DOWN markers specially
      // IMPORTANT: Track indentation level across ALL segments, not per segment
      let trailingLevelDownCount = 0; // Count LEVEL_DOWN markers that appear after all text content
    
    for (const segment of formattedSegments) {
      let processedText = segment.text;
      
      // Process LEVEL markers sequentially to track cumulative indentation level
      while (processedText.includes('§LEVEL_UP§') || processedText.includes('§LEVEL_DOWN§')) {
        const levelUpIndex = processedText.indexOf('§LEVEL_UP§');
        const levelDownIndex = processedText.indexOf('§LEVEL_DOWN§');
        
        let nextIndex = -1;
        let isLevelUp = false;
        
        if (levelUpIndex !== -1 && (levelDownIndex === -1 || levelUpIndex < levelDownIndex)) {
          nextIndex = levelUpIndex;
          isLevelUp = true;
        } else if (levelDownIndex !== -1) {
          nextIndex = levelDownIndex;
          isLevelUp = false;
        }
        
        if (nextIndex === -1) break;
        
        if (isLevelUp) {
          this.currentIndentationLevel++;
          processedText = processedText.replace('§LEVEL_UP§', '');
        } else {
          // Check if this LEVEL_DOWN appears after all meaningful text content in the current paragraph
          const textAfterMarker = processedText.substring(nextIndex + 12); // 12 = length of '§LEVEL_DOWN§'
          const cleanTextAfter = textAfterMarker.trim().replace(/§LEVEL_DOWN§/g, '').replace(/§LEVEL_UP§/g, '').trim();
          
          // Check if the text after this marker is separated by a newline (indicating it's part of the next paragraph)
          const isNextParagraphText = textAfterMarker.startsWith('\n');
          const isTrailing = !cleanTextAfter || isNextParagraphText;
          
          if (isTrailing) {
            // This LEVEL_DOWN is trailing (after text content) - save it for next paragraph
            trailingLevelDownCount++;
            processedText = processedText.replace('§LEVEL_DOWN§', '');
          } else {
            // This LEVEL_DOWN has text after it - apply to current paragraph
            this.currentIndentationLevel = Math.max(0, this.currentIndentationLevel - 1);
            processedText = processedText.replace('§LEVEL_DOWN§', '');
          }
        }
      }
    }
    
    // Use the current class-level indentation for this paragraph (before applying trailing markers)
    paragraphIndent = this.currentIndentationLevel;
    
    // Apply trailing LEVEL_DOWN markers to the class-level state (for next paragraph)
    for (let i = 0; i < trailingLevelDownCount; i++) {
      this.currentIndentationLevel = Math.max(0, this.currentIndentationLevel - 1);
    }
    
    // Second pass: Clean up all segments by removing any remaining markers and adding inline spacing
    for (const segment of formattedSegments) {
      let processedText = segment.text;
      
      // Remove all level markers (already processed above)
      processedText = processedText.replace(/§LEVEL_UP§/g, '');
      processedText = processedText.replace(/§LEVEL_DOWN§/g, '');
      
      // Note: Paragraph breaks are now handled earlier in addTextSections
      
      // Replace INDENT markers with spaces
      processedText = processedText.replace(/§INDENT§/g, '    ');
      
      // Update the segment with processed text
      segment.text = processedText;
    }
    
    // Convert indent levels to actual spacing (12 spaces worth of width per level)
    const indentWidth = paragraphIndent * this.doc.getTextWidth('            '); // 12 spaces
    
    // Process segments with proper text wrapping
    let currentLine: Array<{text: string, bold?: boolean, italic?: boolean, color?: [number, number, number]}> = [];
    let currentLineWidth = 0;
    const lineHeight = this.styles.spacing.line;
    
    for (const segment of formattedSegments) {
      // Handle line breaks in segment text
      const lines = segment.text.split('\n');
      
      for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
        const line = lines[lineIndex];
        
        // If this is not the first line, force a line break
        if (lineIndex > 0) {
              if (currentLine.length > 0) {
                this.renderFormattedLine(currentLine, indentWidth);
                this.currentY += lineHeight;
                currentLine = [];
                currentLineWidth = 0;
              }
        }
        
        // Handle empty lines by adding vertical spacing
        if (!line.trim()) {
          // Empty line - add vertical spacing equivalent to a line
          this.currentY += lineHeight;
          continue;
        }
        
        // Split line by words for proper wrapping
        const words = line.split(' ');
      
      for (let i = 0; i < words.length; i++) {
        const word = words[i];
        const isLastWord = i === words.length - 1;
        const textToMeasure = word + (isLastWord ? '' : ' ');
        
        // Set font to measure correctly
        let fontStyle = 'normal';
        if (segment.bold && segment.italic) {
          fontStyle = 'bolditalic';
        } else if (segment.bold) {
          fontStyle = 'bold';
        } else if (segment.italic) {
          fontStyle = 'italic';
        }
        this.doc.setFont('helvetica', fontStyle);
        this.doc.setFontSize(this.styles.fonts.body);
        
        const wordWidth = this.doc.getTextWidth(textToMeasure);
        
        // Check if word fits on current line (account for indentation in available width)
        if (currentLineWidth + wordWidth > (this.contentWidth - indentWidth) && currentLine.length > 0) {
          // Render current line and start new line
          this.renderFormattedLine(currentLine, indentWidth);
          this.currentY += lineHeight;
          currentLine = [];
          currentLineWidth = 0;
        }
        
        // Add word to current line
        currentLine.push({
          text: textToMeasure,
          bold: segment.bold,
          italic: segment.italic,
          color: segment.color
        });
        currentLineWidth += wordWidth;
        }
      }
    }
    
    // Render final line if it has content
    if (currentLine.length > 0) {
      this.renderFormattedLine(currentLine, indentWidth);
      this.currentY += lineHeight;
    }
    
    this.currentY += this.styles.spacing.paragraph - lineHeight;
  }

  /**
   * Render a line of formatted text segments
   */
  private renderFormattedLine(segments: Array<{text: string, bold?: boolean, italic?: boolean, color?: [number, number, number]}>, indentWidth: number = 0): void {
    this.checkPageBreak(5);
    
    let currentX = this.margin + indentWidth;
    
    for (const segment of segments) {
      // Apply formatting
      this.doc.setTextColor(...(segment.color || this.styles.colors.text));
      this.doc.setFontSize(this.styles.fonts.body);
      
      let fontStyle = 'normal';
      if (segment.bold && segment.italic) {
        fontStyle = 'bolditalic';
      } else if (segment.bold) {
        fontStyle = 'bold';
      } else if (segment.italic) {
        fontStyle = 'italic';
      }
      this.doc.setFont('helvetica', fontStyle);
      
      // Add the text
      this.doc.text(segment.text, currentX, this.currentY);
      currentX += this.doc.getTextWidth(segment.text);
    }
  }

  /**
   * Parse HTML text to extract formatting information
   */
  private parseFormattedText(html: string): Array<{text: string, bold?: boolean, italic?: boolean, color?: [number, number, number]}> {
    
    const segments: Array<{text: string, bold?: boolean, italic?: boolean, color?: [number, number, number]}> = [];
    
    
    // Step 1: Handle HTML entities and blockquote indentation
    let processedHtml = html.replace(/&nbsp;/gi, '§INDENT§') // Use special marker to preserve indentation
                           .replace(/&amp;/gi, '&')
                           .replace(/&lt;/gi, '<')
                           .replace(/&gt;/gi, '>')
                           .replace(/&quot;/gi, '"');

    // Step 1.5: Replace blockquote tags with level markers, handling the specific pattern correctly
    const beforeBlockquote = processedHtml;
    
    // Note: The specific blockquote pattern is now handled earlier in addTextSections
    // where paragraphs are actually split, so no special handling needed here
    
    // First, replace empty blockquotes (only whitespace and/or <br> tags) with line breaks
    // This preserves the line spacing while avoiding the LEVEL_UP/LEVEL_DOWN markers
    processedHtml = processedHtml.replace(/<blockquote[^>]*margin:\s*0\s+0\s+0\s+40px[^>]*>\s*(<br\s*\/?>)*\s*<\/blockquote>/gi, '\n');
    
    // Then replace remaining blockquote tags with level markers
    processedHtml = processedHtml.replace(/<blockquote[^>]*margin:\s*0\s+0\s+0\s+40px[^>]*>/gi, '§LEVEL_UP§');
    processedHtml = processedHtml.replace(/<\/blockquote>/gi, '§LEVEL_DOWN§');
    
    
    // Step 2: Convert div elements to line breaks with special handling for <br>-only divs
    // Each </div> represents the end of a line/paragraph
    processedHtml = processedHtml
      // First, handle special case: <div><br></div> or <div><b><br></b></div> should become single line breaks
      .replace(/<div[^>]*>\s*(<[^>]*>)*\s*<br\s*\/?>\s*(<\/[^>]*>)*\s*<\/div>/gi, '\n')
      // Replace closing </div> tags with line breaks
      .replace(/<\/div>/gi, '\n')
      // Remove opening <div> tags (they don't create line breaks, just containers)
      .replace(/<div[^>]*>/gi, '')
            // Handle standalone <br> tags that aren't inside divs (convert to line breaks)
            .replace(/<br\s*\/?>/gi, '\n')
      // Handle list items as line breaks
      .replace(/<\/li>\s*<li[^>]*>/gi, '\n')
      .replace(/<\/?[uo]l[^>]*>/gi, '\n')
      .replace(/<li[^>]*>/gi, '• ')
      .replace(/<\/li>/gi, '\n');
    
    
    // Step 3: Parse formatting tags while preserving line breaks
    let currentBold = false;
    let currentItalic = false;
    let currentColor: [number, number, number] | undefined = undefined;
    
    // Split by formatting tags but preserve line breaks
    const tokens = processedHtml.split(/(<\/?(?:b|strong|i|em|span[^>]*)>)/gi);
    
    for (const token of tokens) {
      if (token.match(/<(b|strong)>/i)) {
        currentBold = true;
      } else if (token.match(/<\/(b|strong)>/i)) {
        currentBold = false;
      } else if (token.match(/<(i|em)>/i)) {
        currentItalic = true;
      } else if (token.match(/<\/(i|em)>/i)) {
        currentItalic = false;
      } else if (token.match(/<span[^>]*>/i)) {
        const colorMatch = token.match(/color:\s*([^;]+)/i);
        if (colorMatch) {
          currentColor = this.parseColor(colorMatch[1]);
        }
      } else if (token.match(/<\/span>/i)) {
        currentColor = undefined;
      } else if (!token.match(/^<.*>$/)) {
        // This is actual text content (not a tag)
        if (token) {
          // Preserve line breaks and indentation markers, but clean up tabs
          const cleanText = token.replace(/\t/g, ' ');
          
          segments.push({
            text: cleanText,
            bold: currentBold,
            italic: currentItalic,
            color: currentColor
          });
        }
      }
    }
    
    
    return segments;
  }

  /**
   * Parse color string to RGB array
   */
  private parseColor(colorStr: string): [number, number, number] {
    // Handle common color formats
    colorStr = colorStr.trim().toLowerCase();
    
    // RGB format: rgb(r, g, b)
    const rgbMatch = colorStr.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
    if (rgbMatch) {
      return [parseInt(rgbMatch[1]), parseInt(rgbMatch[2]), parseInt(rgbMatch[3])];
    }
    
    // Hex format: #rrggbb
    const hexMatch = colorStr.match(/^#([0-9a-f]{6})$/);
    if (hexMatch) {
      const hex = hexMatch[1];
      return [
        parseInt(hex.substr(0, 2), 16),
        parseInt(hex.substr(2, 2), 16),
        parseInt(hex.substr(4, 2), 16)
      ];
    }
    
    // Common color names
    const colorMap: {[key: string]: [number, number, number]} = {
      'red': [255, 0, 0],
      'green': [0, 128, 0],
      'blue': [0, 0, 255],
      'black': [0, 0, 0],
      'white': [255, 255, 255],
      'gray': [128, 128, 128],
      'grey': [128, 128, 128]
    };
    
    return colorMap[colorStr] || this.styles.colors.text;
  }

  /**
   * Add rich text content with basic formatting support
   */
  private async addRichText(richText: string): Promise<void> {
    if (!richText || richText.trim() === '') {
      this.addParagraph('(No content available)');
      return;
    }

    // Process different types of content
    await this.processRichTextContent(richText);
  }

  /**
   * Process rich text content and handle images, tables, and formatting
   */
  private async processRichTextContent(content: string): Promise<void> {
    
    // Split content by major HTML elements
    const sections = await this.parseHtmlSections(content);
    
    
    for (const section of sections) {
      switch (section.type) {
        case 'paragraph':
          if (section.content.trim()) {
            // Process the paragraph content directly (splitting now handled in addTextSections)
            const htmlContent = section.originalHtml || section.content;
            
            
            await this.addFormattedParagraph(section.content.trim(), htmlContent.trim());
          }
          break;
          
        case 'heading':
          console.log('📝 Adding heading:', section.content);
          this.addSubHeading(section.content, section.level || 4);
          break;
          
        case 'list':
          console.log('📝 Adding list with', section.items?.length || 0, 'items');
          this.addList(section.items || []);
          break;
          
        case 'table':
          console.log('📝 Adding table with', section.data?.length || 0, 'rows');
          this.addTable(section.data || []);
          break;
          
        case 'image':
          console.log('📝 Adding image placeholder:', section.src, section.alt);
          await this.addImagePlaceholder(section.src || '', section.alt || '');
          break;
          
        default:
          if (section.content.trim()) {
            this.addParagraph(section.content);
          }
      }
    }
  }

  /**
   * Parse HTML content into structured sections
   */
  private async parseHtmlSections(html: string): Promise<Array<{type: string, content: string, level?: number, items?: string[], data?: string[][], src?: string, alt?: string, originalHtml?: string}>> {
    const sections = [];
    
    // Remove script and style tags completely
    html = html.replace(/<(script|style)[^>]*>.*?<\/(script|style)>/gis, '');
    
    // Handle images
    const imageRegex = /<img[^>]+src=["']([^"']+)["'][^>]*(?:alt=["']([^"']*)["'])?[^>]*>/gi;
    let match;
    let lastIndex = 0;
    
    while ((match = imageRegex.exec(html)) !== null) {
      // Add content before image
      if (match.index > lastIndex) {
        const beforeContent = html.substring(lastIndex, match.index);
        await this.addTextSections(beforeContent, sections);
      }
      
      // Add image placeholder
      sections.push({
        type: 'image',
        content: '',
        src: match[1],
        alt: match[2] || 'Image'
      });
      
      lastIndex = imageRegex.lastIndex;
    }
    
    // Add remaining content
    if (lastIndex < html.length) {
      const remainingContent = html.substring(lastIndex);
      await this.addTextSections(remainingContent, sections);
    }
    
    return sections;
  }

  /**
   * Add text sections (paragraphs, headings, lists, tables)
   */
  private async addTextSections(html: string, sections: Array<any>): Promise<void> {
    // Handle tables
    const tableRegex = /<table[^>]*>(.*?)<\/table>/gis;
    html = html.replace(tableRegex, (match, tableContent) => {
      const tableData = this.parseTable(tableContent);
      sections.push({
        type: 'table',
        content: '',
        data: tableData
      });
      return '';
    });
    
    // Handle lists
    const listRegex = /<(ul|ol)[^>]*>(.*?)<\/(ul|ol)>/gis;
    html = html.replace(listRegex, (match, listType, listContent) => {
      const items = this.parseList(listContent);
      sections.push({
        type: 'list',
        content: '',
        items: items
      });
      return '';
    });
    
    // Handle headings
    const headingRegex = /<h([1-6])[^>]*>(.*?)<\/h[1-6]>/gis;
    html = html.replace(headingRegex, (match, level, content) => {
      sections.push({
        type: 'heading',
        content: this.stripHtml(content),
        level: parseInt(level) + 2 // Offset to avoid conflicting with main headings
      });
      return '';
    });
    
    // Handle paragraphs and remaining text - improved div/br handling
    
    // First, handle the specific blockquote pattern that needs paragraph splitting
    let processedHtml = html;
    
    // Handle the specific pattern: </blockquote>Text</blockquote><blockquote>\n</blockquote>Text
    // This should split into separate paragraphs while preserving indentation markers
    processedHtml = processedHtml.replace(
      /(<\/blockquote>)([^<]+?)(<\/blockquote><blockquote[^>]*margin:\s*0\s+0\s+0\s+40px[^>]*>\s*\n\s*<\/blockquote>)([^<]*)/gi, 
      '$1$2\n\n$4'  // Keep the first </blockquote> for "Single indentation", split paragraphs
    );
    
    // Then, normalize the HTML by converting divs and breaks to proper separators
    let normalizedHtml = processedHtml
      .replace(/<br\s*\/?>/gi, '\n\n')  // Convert standalone <br> to double newlines
      .replace(/<\/div>\s*<div[^>]*>/gi, '\n')  // Convert </div><div> to newlines  
      .replace(/<div[^>]*>/gi, '\n')  // Convert opening <div> to newlines
      .replace(/<\/div>/gi, '')  // Remove closing </div>
      .replace(/<\/p>\s*<p[^>]*>/gi, '\n\n')  // Convert </p><p> to double newlines
      .replace(/<p[^>]*>/gi, '')  // Remove opening <p>
      .replace(/<\/p>/gi, '\n')  // Convert closing </p> to newlines
      .replace(/&nbsp;/gi, '§INDENT§')  // Preserve indentation markers
      .replace(/&amp;/gi, '&')   // Convert &amp; to &
      .replace(/&lt;/gi, '<')    // Convert &lt; to <
      .replace(/&gt;/gi, '>')    // Convert &gt; to >
      .replace(/&quot;/gi, '"')  // Convert &quot; to "
      .replace(/\n\s*\n\s*\n+/g, '\n\n')  // Collapse multiple newlines to double
      .trim();
    
    
    // Split by double newlines to get paragraphs, then by single newlines within paragraphs
    const paragraphGroups = normalizedHtml.split('\n\n');
    
    paragraphGroups.forEach(group => {
      const cleanText = this.stripHtml(group).trim();
      if (cleanText) {
        // If the group contains single newlines, treat them as line breaks within the paragraph
        const lines = cleanText.split('\n').map(line => line.trim()).filter(line => line);
        if (lines.length > 0) {
          sections.push({
            type: 'paragraph',
            content: lines.join(' '),  // Join lines with spaces for better readability
            originalHtml: group  // Preserve original HTML for formatting
          });
        }
      }
    });
  }

  /**
   * Parse HTML table into 2D array
   */
  private parseTable(tableHtml: string): string[][] {
    const rows: string[][] = [];
    const rowRegex = /<tr[^>]*>(.*?)<\/tr>/gis;
    let rowMatch;
    
    while ((rowMatch = rowRegex.exec(tableHtml)) !== null) {
      const cells: string[] = [];
      const cellRegex = /<(td|th)[^>]*>(.*?)<\/(td|th)>/gis;
      let cellMatch;
      
      while ((cellMatch = cellRegex.exec(rowMatch[1])) !== null) {
        cells.push(this.stripHtml(cellMatch[2]).trim());
      }
      
      if (cells.length > 0) {
        rows.push(cells);
      }
    }
    
    return rows;
  }

  /**
   * Parse HTML list into array of items
   */
  private parseList(listHtml: string): string[] {
    const items: string[] = [];
    const itemRegex = /<li[^>]*>(.*?)<\/li>/gis;
    let match;
    
    while ((match = itemRegex.exec(listHtml)) !== null) {
      const item = this.stripHtml(match[1]).trim();
      if (item) {
        items.push(item);
      }
    }
    
    return items;
  }

  /**
   * Add a sub-heading within content
   */
  private addSubHeading(text: string, level: number): void {
    this.checkPageBreak(10);
    
    // Process level markers to determine indentation for this heading
    let currentLevel = 0;
    let paragraphIndent = 0;
    let cleanText = text;
    
    // Process markers using a much simpler approach to avoid text corruption
    // First, replace all INDENT markers with spaces
    cleanText = cleanText.replace(/§INDENT§/g, '    ');
    
    // Then process LEVEL markers sequentially to track indentation level
    while (cleanText.includes('§LEVEL_UP§') || cleanText.includes('§LEVEL_DOWN§')) {
      const levelUpIndex = cleanText.indexOf('§LEVEL_UP§');
      const levelDownIndex = cleanText.indexOf('§LEVEL_DOWN§');
      
      let nextIndex = -1;
      let isLevelUp = false;
      
      if (levelUpIndex !== -1 && (levelDownIndex === -1 || levelUpIndex < levelDownIndex)) {
        nextIndex = levelUpIndex;
        isLevelUp = true;
      } else if (levelDownIndex !== -1) {
        nextIndex = levelDownIndex;
        isLevelUp = false;
      }
      
      if (nextIndex === -1) break;
      
      if (isLevelUp) {
        currentLevel++;
        cleanText = cleanText.replace('§LEVEL_UP§', '');
      } else {
        currentLevel = Math.max(0, currentLevel - 1);
        cleanText = cleanText.replace('§LEVEL_DOWN§', '');
      }
    }
    
    // Determine paragraph indentation from the final current level
    paragraphIndent = currentLevel;
    
    const indentWidth = paragraphIndent * this.doc.getTextWidth('            '); // 12 spaces per level
    
    this.doc.setTextColor(...this.styles.colors.text);
    this.doc.setFontSize(this.styles.fonts.body + 2);
    this.doc.setFont('helvetica', 'bold');
    
    this.doc.text(cleanText, this.margin + indentWidth, this.currentY);
    this.currentY += this.styles.spacing.paragraph;
  }

  /**
   * Add a list to the document
   */
  private addList(items: string[]): void {
    items.forEach((item, index) => {
      this.checkPageBreak(5);
      
      // Process level markers to determine indentation for this list item
      let currentLevel = 0;
      let itemIndent = 0;
      let cleanItem = item;
      
      // Process markers using a much simpler approach to avoid text corruption
      // First, replace all INDENT markers with spaces
      cleanItem = cleanItem.replace(/§INDENT§/g, '    ');
      
      // Then process LEVEL markers sequentially to track indentation level
      while (cleanItem.includes('§LEVEL_UP§') || cleanItem.includes('§LEVEL_DOWN§')) {
        const levelUpIndex = cleanItem.indexOf('§LEVEL_UP§');
        const levelDownIndex = cleanItem.indexOf('§LEVEL_DOWN§');
        
        let nextIndex = -1;
        let isLevelUp = false;
        
        if (levelUpIndex !== -1 && (levelDownIndex === -1 || levelUpIndex < levelDownIndex)) {
          nextIndex = levelUpIndex;
          isLevelUp = true;
        } else if (levelDownIndex !== -1) {
          nextIndex = levelDownIndex;
          isLevelUp = false;
        }
        
        if (nextIndex === -1) break;
        
        if (isLevelUp) {
          currentLevel++;
          cleanItem = cleanItem.replace('§LEVEL_UP§', '');
        } else {
          currentLevel = Math.max(0, currentLevel - 1);
          cleanItem = cleanItem.replace('§LEVEL_DOWN§', '');
        }
      }
      
      // Determine item indentation from the final current level
      itemIndent = currentLevel;
      
      const indentWidth = itemIndent * this.doc.getTextWidth('            '); // 12 spaces per level
      const availableWidth = this.contentWidth - 10 - indentWidth; // Account for bullet and indentation
      
      this.doc.setTextColor(...this.styles.colors.text);
      this.doc.setFontSize(this.styles.fonts.body);
      this.doc.setFont('helvetica', 'normal');
      
      const bullet = `• `;
      const lines = this.doc.splitTextToSize(`${bullet}${cleanItem}`, availableWidth);
      
      lines.forEach((line: string, lineIndex: number) => {
        this.checkPageBreak(5);
        const x = lineIndex === 0 ? this.margin + 5 + indentWidth : this.margin + 10 + indentWidth;
        this.doc.text(line, x, this.currentY);
        this.currentY += this.styles.spacing.line;
      });
    });
    
    this.addSpacing(this.styles.spacing.paragraph);
  }

  /**
   * Add a table to the document
   */
  private addTable(tableData: string[][]): void {
    if (tableData.length === 0) return;
    
    this.checkPageBreak(20);
    
    // Calculate column widths
    const maxCols = Math.max(...tableData.map(row => row.length));
    const colWidth = (this.contentWidth - 10) / maxCols;
    
    // Add table border
    const tableHeight = tableData.length * 8 + 4;
    this.doc.setDrawColor(200, 200, 200);
    this.doc.rect(this.margin, this.currentY - 2, this.contentWidth, tableHeight);
    
    // Add table content
    tableData.forEach((row, rowIndex) => {
      this.checkPageBreak(10);
      
      // Header row styling
      if (rowIndex === 0) {
        this.doc.setFillColor(240, 240, 240);
        this.doc.rect(this.margin, this.currentY - 2, this.contentWidth, 8, 'F');
        this.doc.setFont('helvetica', 'bold');
      } else {
        this.doc.setFont('helvetica', 'normal');
      }
      
      this.doc.setTextColor(...this.styles.colors.text);
      this.doc.setFontSize(this.styles.fonts.small);
      
      row.forEach((cell, colIndex) => {
        const x = this.margin + 3 + (colIndex * colWidth); // Better padding
        const maxCellWidth = colWidth - 6; // Leave more padding
        let cellText = cell || '';
        
        // Measure and truncate text to fit in cell
        const textWidth = this.doc.getTextWidth(cellText);
        if (textWidth > maxCellWidth) {
          while (this.doc.getTextWidth(cellText + '...') > maxCellWidth && cellText.length > 0) {
            cellText = cellText.slice(0, -1);
          }
          cellText += '...';
        }
        
        this.doc.text(cellText, x, this.currentY + 5); // Better vertical centering
        
        // Add column separators
        if (colIndex < row.length - 1) {
          this.doc.setDrawColor(200, 200, 200);
          this.doc.line(x + colWidth - 1, this.currentY - 2, x + colWidth - 1, this.currentY + 6);
        }
      });
      
      this.currentY += 8;
      
      // Add horizontal row border for all rows
      this.doc.setDrawColor(180, 180, 180);
      this.doc.setLineWidth(0.5);
      this.doc.line(this.margin, this.currentY, this.margin + this.contentWidth, this.currentY);
    });
    
    this.addSpacing(this.styles.spacing.paragraph);
  }

  /**
   * Add image placeholder
   */
  private async addImagePlaceholder(src: string, alt: string): Promise<void> {
    this.checkPageBreak(60); // More space for actual images
    
    if (src) {
      console.log('🖼️ Attempting to load content image:', src);
      
      // Try to load the actual image
      const imageLoaded = await this.loadAndAddImage(src, this.margin + this.contentWidth / 2, this.currentY + 30, this.contentWidth - 20, 50);
      
      if (imageLoaded) {
        // Image loaded successfully, add caption if available
        if (alt) {
          this.currentY += 55; // Space for image
          this.doc.setTextColor(...this.styles.colors.secondary);
          this.doc.setFontSize(this.styles.fonts.small);
          this.doc.setFont('helvetica', 'italic');
          this.doc.text(alt, this.margin, this.currentY, { maxWidth: this.contentWidth });
          this.currentY += 10;
        } else {
          this.currentY += 60; // Just space for image
        }
        this.addSpacing(this.styles.spacing.paragraph);
        return;
      }
    }
    
    // Fallback to placeholder if image loading failed or no src
    this.doc.setDrawColor(180, 180, 180);
    this.doc.setFillColor(250, 250, 250);
    this.doc.rect(this.margin, this.currentY, this.contentWidth, 20, 'FD');
    
    // Add image icon and text
    this.doc.setTextColor(...this.styles.colors.secondary);
    this.doc.setFontSize(this.styles.fonts.small);
    this.doc.setFont('helvetica', 'italic');
    
    const imageText = alt || 'Image';
    this.doc.text(`📷 ${imageText}`, this.margin + 5, this.currentY + 10);
    
    if (src) {
      this.doc.text(`Source: ${src.substring(0, 50)}${src.length > 50 ? '...' : ''}`, this.margin + 5, this.currentY + 15);
    }
    
    this.currentY += 25;
    this.addSpacing(this.styles.spacing.paragraph);
  }

  /**
   * Parse blockquote indentation structure with proper stack tracking
   * Only handles blockquote nesting and text content marking - lets Step 2 handle line breaks
   */
  private parseBlockquoteIndentationStack(html: string): string {
    let result = '';
    let indentLevel = 0;
    let i = 0;
    let atStartOfContent = true; // Flag to track if we're at the start of new content
    
    while (i < html.length) {
      // Check for opening blockquote tag with indentation styling
      const openingMatch = html.substring(i).match(/^<blockquote[^>]*margin:\s*0\s+0\s+0\s+40px[^>]*>/i);
      if (openingMatch) {
        indentLevel++;
        i += openingMatch[0].length;
        // Remove the blockquote tag from output
        continue;
      }
      
      // Check for any other opening blockquote tag (pass through, don't affect indentation)
      const otherOpeningMatch = html.substring(i).match(/^<blockquote[^>]*>/i);
      if (otherOpeningMatch) {
        i += otherOpeningMatch[0].length;
        // Keep non-indentation blockquote tags in output
        result += otherOpeningMatch[0];
        continue;
      }
      
      // Check for closing blockquote tag
      if (html.substring(i).match(/^<\/blockquote>/i)) {
        // Only decrease indent if we have indentation-creating blockquotes open
        if (indentLevel > 0) {
          indentLevel--;
        }
        i += 12; // Length of </blockquote>
        // Remove the closing tag from output (whether it was indentation-creating or not)
        continue;
      }
      
      // Skip all other HTML tags (let Step 2 handle them)
      if (html[i] === '<') {
        const tagEnd = html.indexOf('>', i);
        if (tagEnd !== -1) {
          const tag = html.substring(i, tagEnd + 1);
          result += tag;
          i = tagEnd + 1;
          
          // Check if this tag creates new content boundaries
          if (tag.match(/<\/div>/i) || tag.match(/<br\s*\/?>/i)) {
            atStartOfContent = true;
          }
          continue;
        }
      }
      
      // Regular character - this is actual text content
      const char = html[i];
      
      // If we're at the start of new content and have indentation, add markers
      if (atStartOfContent && indentLevel > 0 && char.trim()) {
        result += '§INDENT§'.repeat(indentLevel);
        atStartOfContent = false;
      } else if (char.trim()) {
        // Any non-whitespace character means we're no longer at start of content
        atStartOfContent = false;
      }
      
      result += char;
      i++;
    }
    
    return result;
  }

  /**
   * Parse blockquote indentation structure properly using a stack-based approach (DEPRECATED)
   */
  private parseBlockquoteIndentation(html: string): string {
    
    let result = '';
    let indentLevel = 0;
    let i = 0;
    let pendingIndent = true; // Flag to indicate we need to add indent markers before next content
    
    while (i < html.length) {
      // Check for opening blockquote tag
      if (html.substring(i).match(/^<blockquote[^>]*>/i)) {
        const match = html.substring(i).match(/^<blockquote[^>]*>/i);
        if (match) {
          indentLevel++;
          i += match[0].length;
          pendingIndent = true; // Next content should be indented
          continue;
        }
      }
      
      // Check for closing blockquote tag
      if (html.substring(i).match(/^<\/blockquote>/i)) {
        indentLevel = Math.max(0, indentLevel - 1);
        i += 12; // Length of </blockquote>
        pendingIndent = true; // Next content should use new indent level
        continue;
      }
      
      // Check for div tags
      if (html.substring(i).match(/^<div[^>]*>/i)) {
        const match = html.substring(i).match(/^<div[^>]*>/i);
        if (match) {
          i += match[0].length;
          // Start a new line for div content
          if (result.length > 0 && !result.endsWith('\n')) {
            result += '\n';
          }
          pendingIndent = true;
          continue;
        }
      }
      
      // Skip closing div tags
      if (html.substring(i).match(/^<\/div>/i)) {
        i += 6; // Length of </div>
        continue;
      }
      
      // Handle br tags
      if (html.substring(i).match(/^<br\s*\/?>/i)) {
        const match = html.substring(i).match(/^<br\s*\/?>/i);
        if (match) {
          i += match[0].length;
          result += '\n';
          pendingIndent = true;
          continue;
        }
      }
      
      // Skip other HTML tags (like span, etc.)
      if (html[i] === '<') {
        const tagEnd = html.indexOf('>', i);
        if (tagEnd !== -1) {
          i = tagEnd + 1;
          continue;
        }
      }
      
      // Regular character - this is actual content
      if (html[i].trim()) { // Only for non-whitespace characters
        // Add pending indentation if needed
        if (pendingIndent) {
          // If we're not at the start of result and don't end with newline, add one
          if (result.length > 0 && !result.endsWith('\n') && !result.endsWith('§INDENT§')) {
            result += '\n';
          }
          result += '§INDENT§'.repeat(indentLevel);
          pendingIndent = false;
        }
      }
      
      result += html[i];
      i++;
    }
    
    // Clean up the result
    result = result
      .replace(/§INDENT§+\s*§INDENT§+/g, (match) => {
        // Consolidate multiple indent markers that got duplicated
        const count = (match.match(/§INDENT§/g) || []).length;
        return '§INDENT§'.repeat(Math.min(count, 10)); // Cap at 10 levels max
      })
      .replace(/\n\s*\n/g, '\n') // Collapse multiple newlines but preserve single ones
      .replace(/§INDENT§+\n§INDENT§+/g, (match) => {
        // Handle indent markers around newlines
        const beforeNewline = match.split('\n')[0];
        const afterNewline = match.split('\n')[1];
        const maxIndent = Math.max(
          (beforeNewline.match(/§INDENT§/g) || []).length,
          (afterNewline.match(/§INDENT§/g) || []).length
        );
        return '§INDENT§'.repeat(maxIndent) + '\n' + '§INDENT§'.repeat(maxIndent);
      })
      .trim();
    
    return result;
  }

  /**
   * Strip HTML tags from text (improved implementation)
   */
  private stripHtml(html: string): string {
    return html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/&nbsp;/g, '§INDENT§')
      .replace(/<[^>]*>/g, '')  // Remove all remaining HTML tags
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/§INDENT§/g, '     ') // Convert indentation markers to 5 spaces each
      .trim();
  }

  /**
   * Add spacing
   */
  private addSpacing(amount: number): void {
    this.currentY += amount;
  }

  /**
   * Check if we need a page break and add one if necessary
   */
  private checkPageBreak(requiredSpace: number): void {
    if (this.currentY + requiredSpace > this.pageHeight - this.margin) {
      this.addNewPage();
    }
  }

  /**
   * Add a new page
   */
  private addNewPage(): void {
    this.doc.addPage();
    this.currentY = this.margin;
    this.currentPageNumber++;
  }

  /**
   * Generate section numbering (1, 1.1, 1.1.1, etc.)
   */
  private generateSectionNumber(level: number, isPageTitle: boolean): string {
    if (level > this.sectionCounters.length) return '';
    
    // Increment counter for current level
    this.sectionCounters[level - 1]++;
    
    // Reset all deeper level counters
    for (let i = level; i < this.sectionCounters.length; i++) {
      this.sectionCounters[i] = 0;
    }
    
    // Build section number string
    const numbers = [];
    for (let i = 0; i < level; i++) {
      if (this.sectionCounters[i] > 0) {
        numbers.push(this.sectionCounters[i]);
      }
    }
    
    return numbers.length > 0 ? numbers.join('.') : '';
  }

  /**
   * Add page numbers to all pages
   */
  private addPageNumbers(): void {
    const totalPages = this.doc.getNumberOfPages();
    
    for (let i = 1; i <= totalPages; i++) {
      this.doc.setPage(i);
      
      this.doc.setTextColor(...this.styles.colors.lightText);
      this.doc.setFontSize(this.styles.fonts.small);
      this.doc.setFont('helvetica', 'normal');
      
      const pageText = `Page ${i} of ${totalPages}`;
      this.doc.text(pageText, this.pageWidth / 2, this.pageHeight - 10, { align: 'center' });
    }
  }

  /**
   * Determine if a tile should be included in table of contents
   */
  private shouldIncludeInTOC(tile: TileData): boolean {
    return [
      'content', 
      'roadmap', 
      'form', 
      'external_link', 
      'nested_page',
      'register', 
      'workflow'
    ].includes(tile.type);
  }
}
